import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import {
    useChangeClientStatus,
    useDeactivationPreview,
    type ClientRecord,
    type ClientStatusOption,
    type SessionHandlingOption,
} from '@/api/clients';
import { SelectField } from './SelectField';

// Full list of statuses the server accepts. When Inactive is picked we
// surface the two-option scheduled-session handling choice; see
// project_client_deactivation_flow memory for the agreed design.
const STATUS_OPTIONS: readonly ClientStatusOption[] = [
    'Active',
    'Pending',
    'Waitlist',
    'Inactive',
];

interface SessionHandlingChoice {
    value: SessionHandlingOption;
    label: string;
    description: string;
}

const SESSION_HANDLING_CHOICES: readonly SessionHandlingChoice[] = [
    {
        value: 'keep',
        label: 'Keep all sessions',
        description: 'No changes to the calendar. Sessions remain as scheduled.',
    },
    {
        value: 'delete',
        label: 'Delete all scheduled sessions',
        description: 'Remove every scheduled session for this client from the calendar, including past scheduled sessions that were never completed. Canceled, Complete, and Finalized sessions are preserved.',
    },
];

interface StatusChangeDialogProps {
    client: ClientRecord;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function StatusChangeDialog({ client, open, onOpenChange }: StatusChangeDialogProps) {
    const mutation = useChangeClientStatus(client.id);
    const [newStatus, setNewStatus] = useState<ClientStatusOption>(() =>
        firstDifferentStatus(client.status),
    );
    const [note, setNote] = useState('');
    const [sessionHandling, setSessionHandling] = useState<SessionHandlingOption>('keep');
    const [acknowledged, setAcknowledged] = useState(false);
    const [successSummary, setSuccessSummary] = useState<{
        status: ClientStatusOption;
        affected: number;
    } | null>(null);

    const isDeactivating = newStatus === 'Inactive';
    const preview = useDeactivationPreview(client.id, open && isDeactivating);
    const notesInProgressCount = preview.data?.notesInProgressCount ?? 0;
    const needsAck =
        isDeactivating && sessionHandling === 'delete' && notesInProgressCount > 0;

    // Reset the form back to sensible defaults each time the dialog opens.
    // We only key off `open` — resetting on `client.status` change would wipe
    // the post-submit success summary, because a successful deactivation
    // writes `status: 'Inactive'` into the React Query cache which propagates
    // back through the `client` prop.
    useEffect(() => {
        if (open) {
            setNewStatus(firstDifferentStatus(client.status));
            setNote('');
            setSessionHandling('keep');
            setAcknowledged(false);
            setSuccessSummary(null);
            mutation.reset();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset the note-in-progress acknowledgement any time the user changes
    // their handling choice — they only need to ack when delete is picked.
    useEffect(() => {
        setAcknowledged(false);
    }, [sessionHandling, newStatus]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (newStatus === client.status) return;
        if (needsAck && !acknowledged) return;
        try {
            const result = await mutation.mutateAsync({
                status: newStatus,
                note: note.trim(),
                ...(newStatus === 'Inactive' ? { sessionHandling } : {}),
            });
            if (newStatus === 'Inactive') {
                setSuccessSummary({
                    status: newStatus,
                    affected: result.affectedSessionCount,
                });
                // Parent closes the dialog via the Done button so the summary
                // stays visible. Other status changes close immediately.
            } else {
                onOpenChange(false);
            }
        } catch {
            /* error is surfaced below via mutation.error */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to change status';
        return 'Unexpected error';
    })();

    const isNoOp = newStatus === client.status;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Change Status</DialogTitle>
                    <DialogDescription>
                        Status changes are logged to this client's history.
                    </DialogDescription>
                </DialogHeader>

                {successSummary ? (
                    <DeactivationSummary
                        summary={successSummary}
                        onClose={() => onOpenChange(false)}
                    />
                ) : (
                    <form onSubmit={onSubmit} noValidate>
                        {serverError && (
                            <Alert variant="danger" className="mb-4">
                                {serverError}
                            </Alert>
                        )}

                        <div className="mb-3.5">
                            <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">
                                Current status
                            </div>
                            <StatusBadge status={client.status} />
                        </div>

                        <FormField label="New status" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value as ClientStatusOption)}
                                >
                                    {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>

                        {isDeactivating && (
                            <div className="mb-4">
                                <Alert variant="warning" className="mb-3">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                        <div>
                                            Deactivating this client ends their access and stops new
                                            billing. Choose how to handle any scheduled sessions.
                                        </div>
                                    </div>
                                </Alert>
                                <fieldset>
                                    <legend className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">
                                        Scheduled sessions
                                    </legend>
                                    <div role="radiogroup" aria-label="Scheduled session handling" className="space-y-2">
                                        {SESSION_HANDLING_CHOICES.map((c) => (
                                            <label
                                                key={c.value}
                                                className="flex items-start gap-2 p-2 rounded-md border border-border hover:bg-surface-muted cursor-pointer"
                                            >
                                                <input
                                                    type="radio"
                                                    name="sessionHandling"
                                                    value={c.value}
                                                    checked={sessionHandling === c.value}
                                                    onChange={() => setSessionHandling(c.value)}
                                                    className="mt-1"
                                                />
                                                <div className="text-sm">
                                                    <div className="font-semibold text-text">{c.label}</div>
                                                    <div className="text-text-muted">{c.description}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>

                                {needsAck && (
                                    <Alert variant="danger" className="mt-3">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <div className="font-semibold">
                                                    {notesInProgressCount} scheduled session
                                                    {notesInProgressCount === 1 ? ' has' : 's have'} a note in progress
                                                </div>
                                                <div className="text-sm mt-0.5">
                                                    Deleting will discard note content that isn't
                                                    finalized. Finalize the notes first if you need
                                                    to keep them.
                                                </div>
                                                <label className="flex items-start gap-2 mt-2 text-sm cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={acknowledged}
                                                        onChange={(e) => setAcknowledged(e.target.checked)}
                                                        className="mt-1"
                                                    />
                                                    <span>I understand — delete them anyway.</span>
                                                </label>
                                            </div>
                                        </div>
                                    </Alert>
                                )}
                            </div>
                        )}

                        <FormField label="Note" hint="Optional — context for why this change was made.">
                            {(id) => (
                                <Textarea
                                    id={id}
                                    rows={3}
                                    placeholder="e.g., Returning after pause; auth gap resolved"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            )}
                        </FormField>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={mutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant={isDeactivating ? 'danger' : 'success'}
                                disabled={
                                    mutation.isPending
                                    || isNoOp
                                    || (needsAck && !acknowledged)
                                }
                            >
                                {mutation.isPending
                                    ? 'Saving…'
                                    : isDeactivating
                                        ? 'Deactivate'
                                        : 'Change Status'}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

function DeactivationSummary({
    summary,
    onClose,
}: {
    summary: { status: ClientStatusOption; affected: number };
    onClose: () => void;
}) {
    const detail =
        summary.affected === 0
            ? 'No scheduled sessions needed to be deleted.'
            : `${summary.affected} scheduled session${summary.affected === 1 ? '' : 's'} deleted.`;
    return (
        <div>
            <Alert variant="success" className="mb-4">
                <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold">Client deactivated</div>
                        <div>{detail}</div>
                    </div>
                </div>
            </Alert>
            <DialogFooter>
                <Button type="button" variant="primary" onClick={onClose}>
                    Done
                </Button>
            </DialogFooter>
        </div>
    );
}

// Pick a default "New status" value that isn't the current one, so the submit
// button isn't immediately disabled when the dialog opens.
function firstDifferentStatus(current: string): ClientStatusOption {
    const found = STATUS_OPTIONS.find((s) => s !== current);
    return found ?? 'Active';
}
